/**
 * Feature Flags Configuration
 *
 * Flags are resolved in priority order:
 * 1. Remote flags from Unleash (via backend /auth/feature-flags endpoint)
 * 2. Static flags from public/config.js (FF_* values)
 * 3. Default: false
 *
 * On-prem/airgapped deployments where the backend can't reach Unleash
 * will fall back to config.js values seamlessly.
 *
 * Usage:
 *   import { isFeatureEnabled, FeatureFlag } from 'src/config/featureFlags';
 *
 *   if (isFeatureEnabled(FeatureFlag.DOCUMENT_INTELLIGENCE)) {
 *     // render feature
 *   }
 *
 *   // Or use the hook for reactive updates
 *   const isEnabled = useFeatureFlag(FeatureFlag.DOCUMENT_INTELLIGENCE);
 */

import axios from "axios";
import { useEffect, useState } from "react";

/**
 * Enum of available feature flags.
 * Add new flags here and in public/config.js (for static fallback).
 */
export enum FeatureFlag {
  /** Document Intelligence - Template-based extraction system */
  DOCUMENT_INTELLIGENCE = "FF_DOCUMENT_INTELLIGENCE",

  /** Interactive User Selection Prompts - Structured input from users */
  /** Remote Resources - External data source integration */

  /** External Model API - Provision cloud LLMs for non-810 clients */
  EXTERNAL_MODEL_API = "FF_EXTERNAL_MODEL_API",

  /** AtomAssist - AI-powered analysis features */
  ATOMASSIST = "FF_ATOMASSIST",
}

/**
 * Maps Unleash flag names (kebab-case) to FeatureFlag enum values.
 * Add entries here when creating new flags in Unleash.
 */
const UNLEASH_TO_LOCAL: Record<string, FeatureFlag> = {
  "document-intelligence": FeatureFlag.DOCUMENT_INTELLIGENCE,
  "external-model-api": FeatureFlag.EXTERNAL_MODEL_API,
  atomassist: FeatureFlag.ATOMASSIST,
};

// Remote flags fetched from backend, keyed by FeatureFlag enum value.
let _remoteFlags: Record<string, boolean> | null = null;
let _fetchPromise: Promise<void> | null = null;
let _listeners: Array<() => void> = [];

/**
 * Fetch feature flags from the backend (which resolves them from Unleash).
 * Call once at app startup. Safe to call multiple times (deduped).
 */
export function initFeatureFlags(): Promise<void> {
  if (_fetchPromise) return _fetchPromise;

  const apiUrl = window.ENV?.NUCLEARN_API_URL || "";
  if (!apiUrl) return Promise.resolve();

  _fetchPromise = axios
    .get<Record<string, boolean>>(`${apiUrl}/auth/feature-flags`)
    .then((res) => res.data)
    .then((flags: Record<string, boolean>) => {
      _remoteFlags = {};
      for (const [unleashName, enabled] of Object.entries(flags)) {
        const localFlag = UNLEASH_TO_LOCAL[unleashName];
        if (localFlag) {
          _remoteFlags[localFlag] = enabled;
        }
      }
      _listeners.forEach((fn) => fn());
    })
    .catch(() => {
      // Silent fallback to config.js flags — expected for airgapped deployments
      _remoteFlags = null;
    });

  return _fetchPromise;
}

/**
 * Check if a feature flag is enabled.
 * Remote (Unleash) flags take priority over config.js static flags.
 * Returns false if the flag is not defined anywhere (safe default).
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  // Remote flags take priority
  if (_remoteFlags && flag in _remoteFlags) {
    return _remoteFlags[flag];
  }
  // Fall back to config.js
  const value = window.ENV?.[flag];
  return value === true;
}

/**
 * Get all feature flags and their current values.
 * Useful for debugging and admin panels.
 */
export function getAllFeatureFlags(): Record<FeatureFlag, boolean> {
  return Object.values(FeatureFlag).reduce(
    (acc, flag) => {
      acc[flag] = isFeatureEnabled(flag);
      return acc;
    },
    {} as Record<FeatureFlag, boolean>,
  );
}

/**
 * React hook for feature flag checks.
 * Re-renders when remote flags are loaded.
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((t) => t + 1);
    _listeners.push(listener);
    return () => {
      _listeners = _listeners.filter((fn) => fn !== listener);
    };
  }, []);

  return isFeatureEnabled(flag);
}
